import React, { useState } from 'react';
import { Upload, X, FileText, Download, ListOrdered, AlignJustify } from 'lucide-react';
import QuillEditorWithImageResize from '../QuillEditorWithImageResize';
import { supabase } from '../../supabase/client';
import { autoSplitNumbers, cleanExtraLines } from '../../utils/cleanCodingHtml';

interface ScratchReferenceAnswerProps {
  programReferenceUrl?: string;
  onProgramReferenceChange?: (url: string) => void;
  answerText?: string;
  onAnswerTextChange?: (text: string, widths?: number[]) => void;
  questionId: string;
  answerImageWidths?: number[];
  showProgramDownload?: boolean; // 是否显示程序下载区域，默认 true（Scratch 专用）
}

/**
 * Scratch 题目参考答案区
 * 分为两个模块：
 * 1. 【程序参考】- 上传 .sb3 文件
 * 2. 【参考答案】- 富文本编辑器，支持图文混排
 */
export default function ScratchReferenceAnswer({
  programReferenceUrl,
  onProgramReferenceChange,
  answerText = '',
  onAnswerTextChange,
  questionId,
  answerImageWidths = [],
  showProgramDownload = true, // 默认显示（Scratch 专用）
}: ScratchReferenceAnswerProps) {
  const [isUploading, setIsUploading] = useState(false);

  // 处理序号自动分行
  const handleAutoSplitNumbers = () => {
    if (!answerText || !onAnswerTextChange) return;
    const cleaned = autoSplitNumbers(answerText);
    onAnswerTextChange(cleaned);
  };

  // 处理清理多余空行
  const handleCleanExtraLines = () => {
    if (!answerText || !onAnswerTextChange) return;
    const cleaned = cleanExtraLines(answerText);
    onAnswerTextChange(cleaned);
  };

  // 处理 .sb3 文件上传
  const handleUploadSb3 = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.name.endsWith('.sb3')) {
      alert('请上传 .sb3 格式的 Scratch 项目文件');
      return;
    }

    setIsUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const arrayBuffer = await file.arrayBuffer();
      const { data, error } = await supabase.storage
        .from('paper-images')
        .upload(`scratch-reference/${fileName}`, arrayBuffer, {
          contentType: file.type || 'application/octet-stream',
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('paper-images')
        .getPublicUrl(data.path);

      onProgramReferenceChange?.(publicUrl);
    } catch (error) {
      console.error('上传失败:', error);
      alert('上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  // 删除 .sb3 文件
  const handleDeleteSb3 = async () => {
    if (!programReferenceUrl) return;

    try {
      // 从 URL 中提取文件路径
      const urlParts = programReferenceUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `scratch-reference/${fileName}`;

      const { error } = await supabase.storage
        .from('paper-images')
        .remove([filePath]);

      if (error) throw error;

      onProgramReferenceChange?.('');
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败，请重试');
    }
  };

  return (
    <div className="space-y-6">
      {/* 程序参考模块 - 仅 Scratch 题目显示 */}
      {showProgramDownload && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            程序下载
          </label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              {isUploading ? '上传中...' : '上传 .sb3 文件'}
              <input
                type="file"
                accept=".sb3"
                onChange={handleUploadSb3}
                disabled={isUploading}
                className="hidden"
              />
            </label>

            {programReferenceUrl && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600 truncate max-w-[200px]">
                  {programReferenceUrl.split('/').pop()}
                </span>
                <a
                  href={programReferenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-gray-200 rounded"
                  title="下载"
                >
                  <Download className="w-3 h-3 text-gray-500" />
                </a>
                <button
                  onClick={handleDeleteSb3}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="删除"
                >
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 参考程序模块 - 富文本编辑器，支持图文混排 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          参考程序
        </label>
        <QuillEditorWithImageResize
          value={answerText}
          onChange={(text, widths) => onAnswerTextChange?.(text, widths)}
          questionId={`${questionId}-ref-answer`}
          targetField="referenceAnswer"
          imageWidths={answerImageWidths}
          applyRealtimeScaling={true}
          placeholder="请输入参考答案，支持图文混排..."
          customToolbarButtons={[
            {
              label: '自动拆分序号分行',
              icon: ListOrdered,
              onClick: handleAutoSplitNumbers,
            },
            {
              label: '清理多余空行',
              icon: AlignJustify,
              onClick: handleCleanExtraLines,
            },
          ]}
        />
      </div>
    </div>
  );
}
